/**
 * Intake BE(spnp) `/v1/revisions/{revision_uuid}/mapping` 경계.
 *
 * **저장 없음(`null`)의 의미는 이 경계가 소유한다**(규칙 §6). BE 는 저장이 없으면 404
 * `mapping.not_found` 로 답한다. 이 경계는 그 응답을 `null` 로 바꾼다. 없는 revision 은 같은 404 라도
 * `revision.not_found` 다. 이 경계는 그 응답을 오류로 남긴다. 두 404 를 상태 코드로 합치지 않는다.
 *
 * **검증은 저장과 별개다.** `validateMapping` 은 편집본을 저장하지 않고 BE 에 보낸다.
 *
 * **편집본을 그대로 보낸다.** 저장 · 검증 · POC 본문의 매핑은 편집본과 같은 값이다. 빈 칸은 `null` 이다.
 * BE 는 빈 칸이 있는 매핑을 임시 저장(`isTemp=true`)으로만 받는다. 일반 저장과 POC 는 완성본만 받고, 아니면
 * 422 `mapping.invalid` 다. 검증은 빈 칸을 `mapping.incomplete` 와 leaf 경로로 알린다. 중복 열은 BE 가 모든
 * 요청에서 422 로 거부한다. 그래서 편집 상태가 보내기 전에 막는다(`feature/mappingEditor.ts` 의
 * `canSendDraft`).
 *
 * **매핑 본문은 정본 스키마만 검사한다**(`contract/mappingSchema.ts`). 매핑 타입이 wire 와 같은 snake_case
 * 라 변환하지 않는다. 모르는 키나 빠진 키가 있으면 계약 오류다. 저장 응답 · 검증 결과 · POC 작업은 스키마가
 * 없어 이 경계가 필드마다 읽고 camelCase 로 바꾼다.
 *
 * **오류 본문의 `issues` 는 값이 없는 칸이 빠져 온다.** 일반 저장 · POC 의 422 `mapping.invalid` 는 고쳐야 하는
 * 자리를 `issues` 로 싣는다. BE 는 오류 본문에서 null 칸을 뺀다. 그래서 `parseErrorIssues` 는 빠진 선택 칸을
 * `null` 로 읽는다. 성공 응답(검증 결과)의 issue 는 모든 키가 있어야 한다(`parseIssue`).
 *
 * Owns:
 * - 매핑 본문 검사, 저장 응답 · 검증 결과 · POC 작업 읽기, "저장 없음" 의 `null` 변환, 첫 저장(201)과
 *   교체(200)의 구분, 취소 전달, 오류 본문의 issue 읽기.
 *
 * Does not own:
 * - 매핑 완성도 · 적용 가능성 — BE 검증(`…/mapping/validate`). 오류 → 문구 — `feature/inspectErrorMessage.ts`.
 */
import {
  apiClient,
  IntakeContractError,
  isHttpClientError,
  readBoolean,
  readBoundedString,
  readInstantMs,
  readInteger,
  readList,
  readRecord,
  readString,
} from "@/shared/http";
import type { SchemaProblem } from "@/shared/json";
import { checkMapping } from "../contract/mappingSchema";
import {
  POC_JOB_STATES,
  type EpisodeValidation,
  type Mapping,
  type MappingIssue,
  type MappingValidation,
  type MappingReview,
  type PocJob,
} from "../domain/mapping";
import type { StoredMapping } from "../domain/storedMapping";

const RESOURCE = "mapping";
const NOT_SAVED_CODE = "mapping.not_found";

type WireObject = Record<string, unknown>;

export type SavedMapping = {
  readonly stored: StoredMapping;
  /** 첫 저장이면 `true`(201), 교체면 `false`(200). */
  readonly created: boolean;
};

export function parseStoredMapping(value: unknown): StoredMapping {
  const body = readRecord(value, RESOURCE, "body");
  return {
    mapping: parseMapping(body.mapping),
    isTemp: readBoolean(body.isTemp, RESOURCE, "isTemp"),
    digest: readString(body.digest, RESOURCE, "digest"),
    updatedAtMs: readInstantMs(body.updated_at, RESOURCE, "updated_at"),
  };
}

/** BE 매핑 JSON → 편집 중인 매핑. 정본 읽기 스키마를 통과한 값만 받는다. */
export function parseMapping(value: unknown): Mapping {
  const checked = checkMapping(value);
  if (!checked.ok) {
    throw new IntakeContractError(RESOURCE, schemaField(checked.problems));
  }
  return checked.value;
}

/** 첫 위반의 경로를 계약 오류의 필드 이름으로 쓴다(예: `mapping.hands.0.side`). */
function schemaField(problems: readonly SchemaProblem[]): string {
  const path = problems[0]?.path ?? "";
  return path === "" ? "mapping" : `mapping.${path}`;
}

/** revision 에 저장된 매핑. 저장한 적이 없으면 `null` 이다. */
export async function getRevisionMapping(
  revisionUuid: string,
  signal?: AbortSignal,
): Promise<StoredMapping | null> {
  try {
    const response = await apiClient.get<unknown>(mappingPath(revisionUuid), { signal });
    return parseStoredMapping(response.data);
  } catch (error) {
    if (isHttpClientError(error) && error.code === NOT_SAVED_CODE) {
      return null;
    }
    throw error;
  }
}

/**
 * 매핑을 저장한다. `isTemp` 는 사람이 고른다. 빈 칸이 있으면 BE 는 임시 저장만 받는다(일반 저장은 422
 * `mapping.invalid`). 저장은 수치 검증을 하지 않는다. 응답은 서버가 보관한 값이다.
 */
export async function saveRevisionMapping(
  revisionUuid: string,
  mapping: Mapping,
  isTemp: boolean,
): Promise<SavedMapping> {
  const response = await apiClient.put<unknown>(mappingPath(revisionUuid), { mapping, isTemp });
  return { stored: parseStoredMapping(response.data), created: response.status === 201 };
}

/**
 * 편집본을 Probe 샘플의 episode 들에 적용해 본다. 저장 · `isTemp` 는 바뀌지 않는다.
 * `sourceIds` 는 하나 이상, 중복 없이 샘플 안의 episode 여야 한다(아니면 422 `mapping.invalid`).
 */
export async function validateMapping(
  revisionUuid: string,
  request: { mapping: Mapping; probeUuid: string; sourceIds: readonly string[] },
): Promise<MappingValidation> {
  const response = await apiClient.post<unknown>(`${mappingPath(revisionUuid)}/validate`, {
    mapping: request.mapping,
    probe_uuid: request.probeUuid,
    source_ids: [...request.sourceIds],
  });
  return parseValidation(response.data);
}

export function parseValidation(value: unknown): MappingValidation {
  const body = readObject(value, "validation");
  return {
    validationUuid: readString(body.validation_uuid, RESOURCE, "validation_uuid"),
    revisionUuid: readString(body.revision_uuid, RESOURCE, "revision_uuid"),
    tcpAssessments: readList(body.tcp_assessments, RESOURCE, "tcp_assessments").map(
      (item, index) => {
        const path = `tcp_assessments.${index}`;
        const row = readObject(item, path);
        return {
          path: readString(row.path, RESOURCE, `${path}.path`),
          status: readBoundedString(
            row.status,
            ["blocked", "requires_validation", "requires_experimental_review"] as const,
            RESOURCE,
            `${path}.status`,
          ),
          issues: readList(row.issues, RESOURCE, `${path}.issues`).map((issue, i) =>
            parseIssue(issue, `${path}.issues.${i}`),
          ),
        };
      },
    ),
    mappingDigest: readString(body.mapping_digest, RESOURCE, "mapping_digest"),
    probeUuid: readString(body.probe_uuid, RESOURCE, "probe_uuid"),
    sampleUuid: readString(body.sample_uuid, RESOURCE, "sample_uuid"),
    sampleManifestDigest: readString(
      body.sample_manifest_digest,
      RESOURCE,
      "sample_manifest_digest",
    ),
    engineVersion: readString(body.engine_version, RESOURCE, "engine_version"),
    templatesVersion: readString(body.templates_version, RESOURCE, "templates_version"),
    implementation: readString(body.implementation, RESOURCE, "implementation"),
    episodes: readList(body.episodes, RESOURCE, "episodes").map((episode, index) =>
      parseEpisodeValidation(episode, `episodes.${index}`),
    ),
  };
}

function parseEpisodeValidation(value: unknown, field: string): EpisodeValidation {
  const row = readObject(value, field);
  return {
    sourceId: readString(row.source_id, RESOURCE, `${field}.source_id`),
    valid: readBoolean(row.valid, RESOURCE, `${field}.valid`),
    issues: readList(row.issues, RESOURCE, `${field}.issues`).map((issue, index) =>
      parseIssue(issue, `${field}.issues.${index}`),
    ),
  };
}

const SEVERITIES = ["error", "warning"] as const;

export function parseIssue(value: unknown, field: string): MappingIssue {
  const row = readObject(value, field);
  const text = (key: string): string | null => readNullableTextField(row[key], `${field}.${key}`);
  return {
    code: readString(row.code, RESOURCE, `${field}.code`),
    path: readString(row.path, RESOURCE, `${field}.path`),
    message: readText(row.message, `${field}.message`),
    severity: readBoundedString(row.severity, SEVERITIES, RESOURCE, `${field}.severity`),
    channel: text("channel"),
    otherChannel: text("other_channel"),
    row: row.row === null ? null : readInteger(row.row, RESOURCE, `${field}.row`),
    timestamp: text("timestamp"),
    otherTimestamp: text("other_timestamp"),
    timestampUnit: text("timestamp_unit"),
    otherTimestampUnit: text("other_timestamp_unit"),
  };
}

/** 오류 본문에서 빠질 수 있는 issue 의 선택 칸. 빠지면 `null` 이다. */
const OPTIONAL_ISSUE_KEYS = [
  "channel",
  "other_channel",
  "row",
  "timestamp",
  "other_timestamp",
  "timestamp_unit",
  "other_timestamp_unit",
] as const;

/**
 * 오류 본문(`HttpClientError.details`)의 `issues`. 키가 없으면 `null` 이다. issue 의 `code` · `path` · `message` ·
 * `severity` 는 있어야 하고, 빠진 선택 칸은 `null` 로 읽는다.
 */
export function parseErrorIssues(details: unknown): MappingIssue[] | null {
  if (typeof details !== "object" || details === null || !("issues" in details)) {
    return null;
  }
  return readList(details.issues, RESOURCE, "error.issues").map((value, index) => {
    const field = `error.issues.${index}`;
    const row = readObject(value, field);
    const defaults = Object.fromEntries(OPTIONAL_ISSUE_KEYS.map((key) => [key, null]));
    return parseIssue({ ...defaults, ...row }, field);
  });
}

/**
 * POC 를 접수한다(202). 편집본을 보낸다. 저장된 매핑이 아니다. 작업은 원본의 앞 3개 episode 를 변환하고
 * 영상 · deep validation 을 한다. 매핑이 완성되지 않았으면 422 `mapping.invalid` 다.
 */
export async function requestPoc(
  revisionUuid: string,
  request: {
    probeUuid: string;
    mapping: Mapping;
    validationUuid: string;
    reviewUuid: string | null;
  },
): Promise<PocJob> {
  const response = await apiClient.post<unknown>(`/v1/revisions/${revisionUuid}/pocs`, {
    probe_uuid: request.probeUuid,
    mapping: request.mapping,
    validation_uuid: request.validationUuid,
    review_uuid: request.reviewUuid,
  });
  return parsePocJob(response.data);
}

/** POC 작업의 지금 상태. 없는 작업은 404 `http.not_found` 다. */
export async function getPoc(
  revisionUuid: string,
  jobUuid: string,
  signal?: AbortSignal,
): Promise<PocJob> {
  const response = await apiClient.get<unknown>(`/v1/revisions/${revisionUuid}/pocs/${jobUuid}`, {
    signal,
  });
  return parsePocJob(response.data);
}

export function parsePocJob(value: unknown): PocJob {
  const body = readObject(value, "poc");
  return {
    validationUuid: readString(body.validation_uuid, RESOURCE, "poc.validation_uuid"),
    reviewUuid: readNullableTextField(body.review_uuid, "poc.review_uuid"),
    experimental: readBoolean(body.experimental, RESOURCE, "poc.experimental"),
    uuid: readString(body.uuid, RESOURCE, "poc.uuid"),
    state: readBoundedString(body.state, POC_JOB_STATES, RESOURCE, "poc.state"),
    attempt: readInteger(body.attempt, RESOURCE, "poc.attempt"),
    createdAtMs: readInstantMs(body.created_at, RESOURCE, "poc.created_at"),
    updatedAtMs: readInstantMs(body.updated_at, RESOURCE, "poc.updated_at"),
    probeUuid: readString(body.probe_uuid, RESOURCE, "poc.probe_uuid"),
    sampleUuid: readString(body.sample_uuid, RESOURCE, "poc.sample_uuid"),
    mappingDigest: readString(body.mapping_digest, RESOURCE, "poc.mapping_digest"),
    inputDigest: readString(body.input_digest, RESOURCE, "poc.input_digest"),
    sourceIds: readList(body.source_ids, RESOURCE, "poc.source_ids").map((id, index) =>
      readString(id, RESOURCE, `poc.source_ids.${index}`),
    ),
    engineVersion: readString(body.engine_version, RESOURCE, "poc.engine_version"),
    templatesVersion: readString(body.templates_version, RESOURCE, "poc.templates_version"),
    implementation: readString(body.implementation, RESOURCE, "poc.implementation"),
    outputArtifactUuid:
      body.output_artifact_uuid === null
        ? null
        : readString(body.output_artifact_uuid, RESOURCE, "poc.output_artifact_uuid"),
    result: readNullableObject(body.result, "poc.result"),
    error: body.error === null ? null : readText(body.error, "poc.error"),
  };
}

/** 사람이 검토한 추론 후보를 기록한다. 서버는 입력·표본·계산 버전 일치를 다시 검사한다. */
export async function reviewMapping(
  revisionUuid: string,
  request: {
    mapping: Mapping;
    probeUuid: string;
    validationUuid: string;
    note: string;
  },
): Promise<MappingReview> {
  const response = await apiClient.post<unknown>(`${mappingPath(revisionUuid)}/reviews`, {
    mapping: request.mapping,
    probe_uuid: request.probeUuid,
    validation_uuid: request.validationUuid,
    note: request.note,
  });
  const body = readObject(response.data, "review");
  return {
    uuid: readString(body.uuid, RESOURCE, "review.uuid"),
    validationUuid: readString(body.validation_uuid, RESOURCE, "review.validation_uuid"),
    reviewerUuid: readString(body.reviewer_uuid, RESOURCE, "review.reviewer_uuid"),
    createdAtMs: readInstantMs(body.created_at, RESOURCE, "review.created_at"),
    note: readString(body.note, RESOURCE, "review.note"),
  };
}

/** 저장된 매핑을 지운다. 저장이 없어도 성공이다(204). */
export async function deleteRevisionMapping(revisionUuid: string): Promise<void> {
  await apiClient.delete(mappingPath(revisionUuid));
}

function mappingPath(revisionUuid: string): string {
  return `/v1/revisions/${revisionUuid}/mapping`;
}

// ── 읽기 ──────────────────────────────────────────────────────────────────

/** 배열도 object 라 `readRecord` 를 통과한다. 객체 자리에 목록이 오면 계약 위반이다. */
function readObject(value: unknown, field: string): WireObject {
  if (Array.isArray(value)) {
    throw new IntakeContractError(RESOURCE, field);
  }
  return readRecord(value, RESOURCE, field);
}

function readNullableObject(value: unknown, field: string): WireObject | null {
  return value === null ? null : readObject(value, field);
}

/** 빈 문자열도 받는 문자열. `readString` 은 빈 값을 거부한다. */
function readText(value: unknown, field: string): string {
  if (typeof value !== "string") {
    throw new IntakeContractError(RESOURCE, field);
  }
  return value;
}

/** issue 의 선택 필드. `null` 과 문자열만 받는다. 빈 문자열은 값 그대로 둔다. */
function readNullableTextField(value: unknown, field: string): string | null {
  return value === null ? null : readText(value, field);
}
